import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../utils/api";
import { resolveBackendAssetUrl } from "../utils/urls";

const R2_BASE_URL = "https://pub-344439af8d8e4c04b9cc9f70d30c8ef8.r2.dev";

const FALLBACK_HERO_SLIDES = [
  {
    src: `${R2_BASE_URL}/8574/F8574-2/1.jpg`,
    alt: "Classic optical frame in acetate",
    title: "Precision Optical",
    description: "Contemporary trade-ready styles designed for daily wear and clinic confidence.",
  },
  {
    src: `${R2_BASE_URL}/8301/SG8301-3/1.jpg`,
    alt: "Premium sunglasses collection",
    title: "Modern Sunglass Edit",
    description: "Statement silhouettes that balance comfort, quality and standout shelf appeal.",
  },
  {
    src: `${R2_BASE_URL}/5949/F5949-3/1.jpg`,
    alt: "Bold eyewear profile",
    title: "Distinctive Profiles",
    description: "Curated frame shapes to help independent practices offer unique variety.",
  },
  {
    src: `${R2_BASE_URL}/7810/F7810-1/1.jpg`,
    alt: "Premium wholesale eyewear",
    title: "Trade Collection",
    description: "High-margin options with reliable stock flow and responsive fulfilment.",
  },
];

const SWIPE_THRESHOLD = 45;

const mapFeaturedProductToSlide = (product) => {
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const primaryImage = productImages.find((image) => image?.is_primary) || productImages[0];
  const imageUrl = resolveBackendAssetUrl(primaryImage?.url);

  if (!imageUrl) {
    return null;
  }

  return {
    src: imageUrl,
    alt: product.name || "Featured eyewear",
    title: product.name || "Featured Collection",
    description: product.description || "Premium eyewear selected for independent optical stores.",
  };
};

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState(FALLBACK_HERO_SLIDES);
  const [touchStartX, setTouchStartX] = useState(null);
  const carouselSlides = heroSlides.flatMap((slide, index) => [
    { type: "intro", id: `intro-${index}` },
    { ...slide, type: "image", id: `${slide.src}-${index}` },
  ]);

  useEffect(() => {
    const fetchFeaturedSlides = async () => {
      try {
        const { data } = await api.get("/products", {
          params: {
            isFeatured: true,
            limit: 8,
          },
        });

        const mappedSlides = (data?.products || [])
          .map(mapFeaturedProductToSlide)
          .filter(Boolean)
          .slice(0, 6);

        setHeroSlides(mappedSlides.length > 0 ? mappedSlides : FALLBACK_HERO_SLIDES);
      } catch (error) {
        console.error("Error loading featured slideshow products:", error);
      }
    };

    fetchFeaturedSlides();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((currentSlide) => (currentSlide + 1) % carouselSlides.length);
    }, 6200);

    return () => clearInterval(intervalId);
  }, [carouselSlides.length]);

  useEffect(() => {
    if (activeSlide > carouselSlides.length - 1) {
      setActiveSlide(0);
    }
  }, [activeSlide, carouselSlides.length]);

  const nextSlide = () => {
    setActiveSlide((currentSlide) => (currentSlide + 1) % carouselSlides.length);
  };

  const previousSlide = () => {
    setActiveSlide((currentSlide) => (currentSlide - 1 + carouselSlides.length) % carouselSlides.length);
  };

  const handleTouchStart = (event) => {
    const touchPoint = event.changedTouches?.[0];
    if (!touchPoint) {
      return;
    }

    setTouchStartX(touchPoint.clientX);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null) {
      return;
    }

    const touchPoint = event.changedTouches?.[0];
    if (!touchPoint) {
      setTouchStartX(null);
      return;
    }

    const deltaX = touchPoint.clientX - touchStartX;

    if (deltaX > SWIPE_THRESHOLD) {
      previousSlide();
    }

    if (deltaX < -SWIPE_THRESHOLD) {
      nextSlide();
    }

    setTouchStartX(null);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[calc(100vh-4.5rem)] items-start justify-center overflow-hidden px-4 pb-14 pt-8 sm:min-h-[calc(100vh-5rem)] sm:pb-16 sm:pt-10">
        <div className="absolute inset-0 bg-gradient-dark opacity-80"></div>
        <motion.div
          className="absolute -left-24 top-24 h-64 w-64 rounded-full bg-gold-500/15 blur-3xl"
          animate={{ scale: [1, 1.18, 1], opacity: [0.35, 0.7, 0.35] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -right-20 bottom-20 h-72 w-72 rounded-full bg-white/10 blur-3xl"
          animate={{ scale: [1.15, 0.95, 1.15], opacity: [0.25, 0.5, 0.25] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Hero Content */}
        <div className="relative z-10 mx-auto w-full max-w-7xl px-2 sm:px-4">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative mx-auto max-w-5xl"
            >
              <div>
                <div className="relative h-[24rem] overflow-hidden rounded-[1.5rem] sm:h-[28rem]" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                  {carouselSlides.map((slide, index) => (
                    <motion.div
                      key={slide.id}
                      className={`absolute inset-0 ${index === activeSlide ? "z-20 pointer-events-auto" : "z-0 pointer-events-none"}`}
                      animate={{
                        opacity: index === activeSlide ? 1 : 0,
                        scale: index === activeSlide ? 1 : 1.03,
                      }}
                      transition={{ duration: 1.1, ease: "easeInOut" }}
                    >
                      {slide.type === "intro" ? (
                        <div className="relative flex h-full flex-col items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black px-5 text-center sm:px-10">
                          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }} className="mb-4 flex justify-center sm:mb-6">
                            <img src="/logo.png" alt="Tabram Eyewear" className="w-full max-w-[12rem] object-contain sm:max-w-[22rem]" />
                          </motion.div>

                          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.08 }} className="mb-3 text-3xl font-display font-bold leading-tight sm:text-5xl md:text-6xl">
                            <span className="heading-metallic">Wholesale Eyewear</span>
                            <br />
                            <span className="text-white">Made Simple</span>
                          </motion.h1>

                          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.16 }} className="mx-auto max-w-2xl text-sm text-gray-300 sm:text-lg md:text-xl">
                            Supplying Independent Opticians Across Ireland & the UK
                          </motion.p>

                          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.24 }} className="mt-6 flex flex-col gap-3 sm:flex-row">
                            <Link to="/register" className="btn-primary inline-block">
                              Apply for Trade Account
                            </Link>
                            <Link to="/optical" className="btn-secondary inline-block">
                              Browse Optical Frames
                            </Link>
                          </motion.div>
                        </div>
                      ) : (
                        <>
                          <img src={slide.src} alt={slide.alt} className="absolute inset-0 h-full w-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/10" />

                          <motion.div
                            key={slide.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="absolute bottom-0 left-0 right-0 p-5 sm:p-6"
                          >
                            <p className="text-xs uppercase tracking-[0.35em] text-gold-200">Gallery Highlight</p>
                            <h3 className="mt-2 text-xl font-display font-semibold text-white sm:text-2xl">{slide.title}</h3>
                            <p className="mt-1 text-sm text-gray-200 sm:text-base">{slide.description}</p>
                          </motion.div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {carouselSlides.map((slide, index) => (
                      <button
                        key={slide.id}
                        type="button"
                        onClick={() => setActiveSlide(index)}
                        className={`h-2.5 rounded-full transition-all ${
                          index === activeSlide ? "w-8 bg-gold-400" : "w-2.5 bg-white/40 hover:bg-white/60"
                        }`}
                        aria-label={`Show slide ${index + 1}`}
                      />
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={previousSlide}
                      className="rounded-full border border-white/25 bg-black/35 px-3 py-1.5 text-sm text-white transition-colors hover:border-gold-300 hover:text-gold-200"
                      aria-label="Previous slide"
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      onClick={nextSlide}
                      className="rounded-full border border-white/25 bg-black/35 px-3 py-1.5 text-sm text-white transition-colors hover:border-gold-300 hover:text-gold-200"
                      aria-label="Next slide"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <div className="pointer-events-none mt-5 overflow-hidden">
                <motion.div
                  className="flex min-w-max gap-3"
                  animate={{ x: [0, -960] }}
                  transition={{ repeat: Infinity, duration: 44, ease: "linear" }}
                >
                  {[...heroSlides, ...heroSlides, ...heroSlides].map((slide, index) => (
                    <div key={`${slide.src}-${index}`} className="h-20 w-28 flex-shrink-0 overflow-hidden rounded-xl border border-white/15 sm:h-24 sm:w-36">
                      <img src={slide.src} alt="Frame preview" className="h-full w-full object-cover" />
                    </div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-black-light py-14 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="card text-center">
                <div className="text-gold-500 text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-14 sm:py-20">
        <div className="container mx-auto px-4">
          <h2 className="section-heading text-center">Our Collections</h2>

          <div className="mt-10 grid grid-cols-1 gap-6 md:mt-12 md:grid-cols-2 md:gap-8">
            {categories.map((category, index) => (
              <Link key={index} to={category.path} className="group relative h-72 overflow-hidden rounded-lg sm:h-80 md:h-96">
                <img
                  src={category.image}
                  alt={category.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-dark opacity-60 group-hover:opacity-40 transition-opacity"></div>
                <motion.div whileHover={{ scale: 1.05 }} className="absolute inset-0 flex items-center justify-center">
                  <h3 className="px-4 text-center text-2xl font-display font-bold text-white sm:text-3xl md:text-4xl">{category.name}</h3>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const features = [
  {
    icon: "💰",
    title: "Low, Trade Prices",
    description: "Competitive wholesale pricing for independent opticians with transparent pricing.",
  },
  {
    icon: "🚚",
    title: "Fast Direct Delivery",
    description: "Free shipping on all trade orders across Ireland and the UK.",
  },
  {
    icon: "📦",
    title: "No Minimum Order",
    description: "Flexible ordering to suit your business needs, from single pieces to bulk orders.",
  },
];

const categories = [
  {
    name: "Optical Frames",
    path: "/optical",
    image: `${R2_BASE_URL}/8574/F8574-2/1.jpg`,
  },
  {
    name: "Sunglasses",
    path: "/sunglasses",
    image: `${R2_BASE_URL}/8301/SG8301-3/1.jpg`,
  },
];

export default Home;
